/**
 * Xendit payment method types
 */
export enum XenditPaymentMethodType {
  BANK_TRANSFER = 'BANK_TRANSFER',
  CREDIT_CARD = 'CREDIT_CARD',
  EWALLET = 'EWALLET',
  RETAIL_OUTLET = 'RETAIL_OUTLET',
  QR_CODE = 'QR_CODE',
  DIRECT_DEBIT = 'DIRECT_DEBIT',
  PAYLATER = 'PAYLATER',
}

/**
 * Payment method display names in Indonesian
 */
export const PAYMENT_METHOD_LABELS: Record<XenditPaymentMethodType, string> = {
  [XenditPaymentMethodType.BANK_TRANSFER]: 'Transfer Bank',
  [XenditPaymentMethodType.CREDIT_CARD]: 'Kartu Kredit / Debit',
  [XenditPaymentMethodType.EWALLET]: 'E-Wallet',
  [XenditPaymentMethodType.RETAIL_OUTLET]: 'Outlet Retail',
  [XenditPaymentMethodType.QR_CODE]: 'Kode QR',
  [XenditPaymentMethodType.DIRECT_DEBIT]: 'Debit Langsung',
  [XenditPaymentMethodType.PAYLATER]: 'Bayar Nanti',
}

/**
 * Converts a Xendit payment method code to a human-readable Indonesian label.
 *
 * @param paymentMethod - The payment method code from Xendit
 * @returns Human-readable payment method name in Indonesian
 *
 * @example
 * ```typescript
 * xenditPaymentMethod('BANK_TRANSFER') // Returns: 'Transfer Bank'
 * xenditPaymentMethod('EWALLET') // Returns: 'E-Wallet'
 * ```
 */
export function xenditPaymentMethod(paymentMethod: string): string {
  // Check if it's a known payment method
  if (paymentMethod in PAYMENT_METHOD_LABELS) {
    return PAYMENT_METHOD_LABELS[paymentMethod as XenditPaymentMethodType]
  }

  // Return the original value if not found (fallback)
  return paymentMethod
}

/**
 * Type guard to check if a string is a valid Xendit payment method
 *
 * @param value - The value to check
 * @returns True if the value is a valid payment method type
 */
export function isValidPaymentMethod(value: string): value is XenditPaymentMethodType {
  return Object.values(XenditPaymentMethodType).includes(value as XenditPaymentMethodType)
}

/**
 * Gets all available payment methods
 *
 * @returns Array of all payment method types
 */
export function getAllPaymentMethods(): XenditPaymentMethodType[] {
  return Object.values(XenditPaymentMethodType)
}

/**
 * Gets all payment method labels
 *
 * @returns Array of objects containing method type and label
 */
export function getAllPaymentMethodLabels(): { type: XenditPaymentMethodType; label: string }[] {
  return Object.entries(PAYMENT_METHOD_LABELS).map(([type, label]) => ({
    type: type as XenditPaymentMethodType,
    label,
  }))
}
